/**
 * Unified Auth — post-login routing (design §5.3, RES-89).
 *
 * `RootNavigator` no longer routes on the binary `user.roles.length > 0`
 * discriminator. It routes on the chosen `activeContext.kind`, showing the
 * picker when the account has more than one available context and an
 * error/sign-out state when it somehow has none.
 *
 * Pure function so the routing decision is unit-testable without mounting the
 * (very heavy) navigator tree. `availableContexts` is authoritative server data
 * from the login response; it is persisted, so a session restore rehydrates it
 * directly (no client-side reconstruction, no extra fetch).
 */
import type { AvailableContext } from '@/types/auth';

/** Abstract destination for the authenticated shell. */
export type AuthRoute = 'admin' | 'resident' | 'picker' | 'no-access';

/**
 * Decide where an authenticated user lands.
 *
 * - A chosen `activeContext` wins (auto-set when there is exactly one context,
 *   or set by the picker tap).
 * - More than one context and nothing chosen yet → the picker.
 * - Zero contexts → the no-access / sign-out state
 *   (design §5.1: "No active access; contact your property manager").
 */
export const resolveAuthRoute = (auth: {
  activeContext: AvailableContext | null;
  availableContexts: AvailableContext[];
}): AuthRoute => {
  if (auth.activeContext?.kind === 'admin') return 'admin';
  if (auth.activeContext?.kind === 'resident') return 'resident';

  const contexts = auth.availableContexts ?? [];
  if (contexts.length > 1) return 'picker';
  // Exactly one context is auto-selected into `activeContext` by the slice, so
  // reaching here with length 1 is only a transient render; treat it as its kind.
  if (contexts.length === 1) return contexts[0].kind;
  return 'no-access';
};
